import { computed, defineComponent, onBeforeUnmount, ref, onMounted } from 'vue';
import { ElDropdown, ElSelect, ElIcon } from 'element-plus';
import { ArrowDown, Signout, Translate, ClassificationSquare } from '@wakeadmin/icons';
import { useInject } from '@wakeadmin/framework';
import { useI18n } from 'vue-i18n';

import { getHeaderDropdowns, HeaderDropdownItemDesc, subscribeHeaderDropdownChange } from '@/services';
import { useBayModel, useAsset } from '@/hooks';
import { DEFAULT_LANGUAGE_IDENTIFIER, SWITCH_IDENTITY_IDENTIFIER } from '@/constants';
import { getMenuI18nKey } from '@/utils';
import { useRoute } from 'vue-router';
import { Icon } from '../Icon';

import AVATAR_DEFAULT from './avatar-default.png';
import ALARME_MESSAGE from './alarme-message.png';

/**
 * 内置指令
 */
const enum BuiltinCommands {
  EXIT = 'exit',
  SWITCH_LANGUAGE = 'switch-language',
}

interface SelectOption {
  value: number;
  label: string;
}

interface DropdownMethods {
  handleClose: () => void;
  handleOpen: () => void;
}

export const DropdownMenus = defineComponent({
  name: 'DropdownMenus',
  setup() {
    const route = useRoute();
    const { t } = useI18n();
    const bay = useBayModel();
    const dropdownRef = ref<DropdownMethods>();
    const i18nDropdownRef = ref<DropdownMethods>();
    const identityList = ref<SelectOption[]>();
    const defaultAvatar = useAsset('IMG_BAY_AVATAR', AVATAR_DEFAULT);
    const larmBell = useAsset('IMG_BAY_LARMBELL', ALARME_MESSAGE);
    const extendsMenus = ref(getHeaderDropdowns());
    const dispose = subscribeHeaderDropdownChange(() => {
      extendsMenus.value = getHeaderDropdowns();
    });
    const i18nInstance = useInject('DI.bay.i18n');

    // 切换语言
    const handleLanguageSwitchCommand = (locale: string) => {
      i18nInstance.value.setLocale(locale);

      // WARN: 刷新页面, 目前这种兼容性最好
      window.location.reload();
    };

    onBeforeUnmount(dispose);

    const handleCommand = (name: string) => {
      switch (name) {
        case BuiltinCommands.EXIT:
          bay.logout();
          dropdownRef.value?.handleClose();
          break;
        case BuiltinCommands.SWITCH_LANGUAGE:
          i18nDropdownRef.value?.handleOpen();
          break;
        default: {
          let menuItem: HeaderDropdownItemDesc | undefined;
          if (extendsMenus.value.length && (menuItem = extendsMenus.value.find(i => i.id === name))) {
            // 扩展菜单点击
            menuItem.onClick?.();
          } else {
            // 扩展路由
            bay.openByIdentifierPath(name);
          }

          // 手动隐藏菜单
          dropdownRef.value?.handleClose();
          break;
        }
      }
    };
    const unreadQuantity = ref(0);

    const languageMenu = computed(() => {
      return bay.menu!.roots.find(item => item.identifier === DEFAULT_LANGUAGE_IDENTIFIER);
    });

    const identityMenu = computed(() => {
      return bay.menu!.roots.find(item => item.identifier === SWITCH_IDENTITY_IDENTIFIER);
    });

    const viewAlarmMessages = () => {
      window.dispatchEvent(
        new CustomEvent('myEvent', {
          detail: { message: '基座告警' },
        })
      );
    };

    // 切换身份请求
    const switchOrgFetch = {
      async list() {
        const data = await bay.getCurrentEmployeeIdentityList();
        identityList.value = data;
      },
      async change(orgId: number) {
        await bay.switchOrg(orgId);
        window.location.reload();
      },
    };

    onMounted(() => {
      // 在主应用中
      window.addEventListener('myCustomEvent', (event: any) => {
        const { data } = event.detail;
        unreadQuantity.value = data;
      });
      console.log('我执行了--bay', route.fullPath);

      switchOrgFetch.list();
    });

    return () => {
      const name = bay.sessionInfo?.userInfo.userName;
      const avatar = defaultAvatar.value;
      // 多语言/切换身份菜单单独进行渲染
      const buttons = bay.menu?.topButtons?.filter(
        item => ![DEFAULT_LANGUAGE_IDENTIFIER, SWITCH_IDENTITY_IDENTIFIER].includes(item.identifier)
      );

      const i18nMenu = languageMenu.value;
      const _identityMenu = identityMenu.value;
      const curChooseOrgId = bay.sessionInfo?.userInfo.curChooseOrgId ?? 0;

      return (
        <div class="bay-header-leftBox">
          {_identityMenu && (
            <ElSelect modelValue={curChooseOrgId} onChange={switchOrgFetch.change} class="ep-org">
              {identityList.value?.map(item => (
                <ElSelect.Option value={item.value} key={item.value} label={item.label} />
              ))}
            </ElSelect>
          )}

          <div
            class="bay-header-icon"
            style={{ display: route.fullPath?.includes('wd-cdp') ? 'flex' : 'none' }}
            onClick={() => viewAlarmMessages()}
          >
            <img src={larmBell.value} alt="" />
            <span style={{ display: unreadQuantity.value !== 0 ? 'block' : 'none' }}>{unreadQuantity.value}</span>
          </div>
          <ElDropdown
            size="large"
            placement="bottom-end"
            onCommand={handleCommand}
            trigger="click"
            ref={dropdownRef}
            hideOnClick={false}
            v-slots={{
              dropdown: () => (
                <ElDropdown.DropdownMenu>
                  {/* 子应用扩展 */}
                  {extendsMenus.value.map(menu => {
                    return (
                      <ElDropdown.DropdownItem command={menu.id} key={menu.id}>
                        <ElIcon>
                          <Icon
                            icon={(typeof menu.icon === 'function' ? menu.icon() : menu.icon) ?? ClassificationSquare}
                          />
                        </ElIcon>
                        {typeof menu.title === 'function' ? menu.title() : menu.title}
                      </ElDropdown.DropdownItem>
                    );
                  })}

                  {i18nMenu && (
                    <ElDropdown.DropdownItem
                      command={BuiltinCommands.SWITCH_LANGUAGE}
                      icon={i18nMenu.icon || Translate}
                    >
                      <ElDropdown
                        onCommand={handleLanguageSwitchCommand}
                        placement="left-start"
                        showTimeout={50}
                        trigger="click"
                        ref={i18nDropdownRef}
                        v-slots={{
                          dropdown: () => (
                            <ElDropdown.DropdownMenu>
                              {i18nMenu.children.map(i => {
                                return (
                                  <ElDropdown.DropdownItem key={i.identifier} command={i.identifier}>
                                    {i.name}
                                  </ElDropdown.DropdownItem>
                                );
                              })}
                            </ElDropdown.DropdownMenu>
                          ),
                        }}
                      >
                        {t('bay.i18n')}
                      </ElDropdown>
                    </ElDropdown.DropdownItem>
                  )}

                  {/* 扩展 */}
                  {buttons?.map(button => {
                    return (
                      <ElDropdown.DropdownItem key={button.uid} command={button.identifierPath}>
                        <ElIcon>
                          <Icon icon={button.icon ?? ClassificationSquare} />
                        </ElIcon>
                        {t(getMenuI18nKey(button.identifierPath), button.name)}
                      </ElDropdown.DropdownItem>
                    );
                  })}
                  <ElDropdown.DropdownItem command={BuiltinCommands.EXIT} icon={Signout}>
                    {t('bay.logout')}
                  </ElDropdown.DropdownItem>
                </ElDropdown.DropdownMenu>
              ),
            }}
          >
            <div class="bay-header__dropdown">
              <img class="bay-header__dropdown-img" src={avatar} alt="avatar" />
              <span class="bay-header__dropdown-name">{name}</span>
              <ElIcon>
                <ArrowDown />
              </ElIcon>
            </div>
          </ElDropdown>
        </div>
      );
    };
  },
});
